/**
 * EA Progress SignalR Hub Connection Manager
 * Manages real-time progress updates for EA analysis jobs
 *
 * Hub URL: wss://DataFeed:5100/hubs/ea-progress (direct connection)
 * Note: SignalR connects directly to DataFeed, not through FIX Engine proxy
 *
 * Methods:
 *   - Subscribe(jobId) - Subscribe to job progress updates
 *   - Unsubscribe(jobId) - Unsubscribe from job updates
 *
 * Events:
 *   - OnProgress(jobId, progress) - Progress update (0-100, chunks completed)
 *   - OnComplete(jobId, result) - Job completed successfully
 *   - OnError(jobId, error) - Job failed with error
 *   - OnJobCancelled(jobId) - Job was cancelled
 */

import * as signalR from "@microsoft/signalr";
import { dataFeedURL } from '../config';

// Connection instance (singleton)
let connection = null;
let connectionPromise = null;

// Event handlers registry
const eventHandlers = {
    onProgress: new Map(),
    onComplete: new Map(),
    onError: new Map(),
    onCancelled: new Map()
};

/**
 * Get or create the SignalR connection
 * @returns {Promise<signalR.HubConnection>}
 */
const getConnection = async () => {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
        return connection;
    }

    if (connectionPromise) {
        return connectionPromise;
    }

    connectionPromise = createConnection();
    return connectionPromise;
};

/**
 * Create a new SignalR connection
 * @returns {Promise<signalR.HubConnection>}
 */
const createConnection = async () => {
    // QAI auth uses 'qai_token', main app uses 'token' - check both
    const token = localStorage.getItem('qai_token') || localStorage.getItem('token');
    // Connect directly to DataFeed SignalR hub (not through FIX Engine proxy)
    const hubUrl = dataFeedURL.replace(/\/$/, '') + '/hubs/ea-progress';

    console.log('[EA Progress] Creating connection to:', hubUrl);
    console.log('[EA Progress] Token available:', !!token);

    connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
            accessTokenFactory: () => token
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

    // Register event handlers
    // OnProgress: Backend may send either (jobId, progressData) or (jobId, progress, stage, ...)
    connection.on('OnProgress', (jobId, progressOrData, stage, chunksCompleted, totalChunks) => {
        console.log(`[EA Progress] Job ${jobId}: Raw progress update`, { progressOrData, stage, chunksCompleted, totalChunks });
        const handler = eventHandlers.onProgress.get(jobId);
        if (handler) {
            let normalizedData;

            // Normalize to object format expected by useEAAsyncAnalysis
            if (typeof progressOrData === 'object' && progressOrData !== null) {
                // Backend sent structured object - normalize PascalCase to camelCase
                // Backend sends: { Progress, Stage, ChunksCompleted, TotalChunks, Message }
                normalizedData = {
                    progress: progressOrData.Progress ?? progressOrData.progress ?? 0,
                    stage: progressOrData.Stage ?? progressOrData.stage ?? null,
                    chunksCompleted: progressOrData.ChunksCompleted ?? progressOrData.chunksCompleted ?? 0,
                    totalChunks: progressOrData.TotalChunks ?? progressOrData.totalChunks ?? 0,
                    message: progressOrData.Message ?? progressOrData.message ?? null
                };
            } else {
                // Backend sent individual params - build object
                normalizedData = {
                    progress: progressOrData ?? 0,
                    stage: stage || null,
                    chunksCompleted: chunksCompleted ?? 0,
                    totalChunks: totalChunks ?? 0
                };
            }

            console.log(`[EA Progress] Job ${jobId}: Normalized progress`, normalizedData);
            handler(normalizedData);
        }
    });

    connection.on('OnComplete', (jobId, data) => {
        console.log(`[EA Progress] Job ${jobId}: OnComplete received - raw data:`, data);
        console.log(`[EA Progress] Job ${jobId}: Raw data keys:`, data ? Object.keys(data) : 'null');
        const handler = eventHandlers.onComplete.get(jobId);
        if (handler) {
            // Backend sends full payload: { jobId, eventType, status, progress, result, tokensUsed, processingTimeMs }
            // The 'result' field contains: { bugs, enhancements, quality, summary, ... }
            // Handle both: (1) new format with nested result, (2) old format where data IS the result
            const normalizedResult = {
                result: data?.result ?? data?.Result ?? data,
                tokensUsed: data?.tokensUsed ?? data?.TokensUsed ?? 0,
                processingTimeMs: data?.processingTimeMs ?? data?.ProcessingTimeMs ?? 0
            };
            console.log(`[EA Progress] Job ${jobId}: Normalized complete - hasResult:`, !!normalizedResult.result,
                'resultKeys:', normalizedResult.result ? Object.keys(normalizedResult.result) : []);
            handler(normalizedResult);
        }
    });

    connection.on('OnError', (jobId, error, errorCode, retryable) => {
        console.error(`[EA Progress] Job ${jobId}: Error`, { error, errorCode, retryable });
        const handler = eventHandlers.onError.get(jobId);
        if (handler) {
            // Normalize error structure
            handler({
                error: typeof error === 'string' ? error : error?.message || 'Analysis failed',
                errorCode: errorCode || error?.errorCode,
                retryable: retryable ?? error?.retryable ?? true
            });
        }
    });

    connection.on('OnJobCancelled', (jobId) => {
        console.log(`[EA Progress] Job ${jobId}: Cancelled`);
        const handler = eventHandlers.onCancelled.get(jobId);
        if (handler) handler();
    });

    // Handle connection state changes
    connection.onreconnecting((error) => {
        console.warn('[EA Progress] Reconnecting...', error);
    });

    connection.onreconnected((connectionId) => {
        console.log('[EA Progress] Reconnected:', connectionId);
        // Re-subscribe to all active jobs
        resubscribeAll();
    });

    connection.onclose((error) => {
        console.log('[EA Progress] Connection closed', error);
        connectionPromise = null;
    });

    try {
        await connection.start();
        console.log('[EA Progress] Connected to hub');
        return connection;
    } catch (error) {
        console.error('[EA Progress] Failed to connect:', error);
        connectionPromise = null;
        throw error;
    }
};

/**
 * Re-subscribe to all active jobs after reconnection
 */
const resubscribeAll = async () => {
    const activeJobs = new Set([
        ...eventHandlers.onProgress.keys(),
        ...eventHandlers.onComplete.keys(),
        ...eventHandlers.onError.keys(),
        ...eventHandlers.onCancelled.keys()
    ]);

    for (const jobId of activeJobs) {
        try {
            await connection.invoke('Subscribe', jobId);
            console.log(`[EA Progress] Re-subscribed to job: ${jobId}`);
        } catch (error) {
            console.error(`[EA Progress] Failed to re-subscribe to job ${jobId}:`, error);
        }
    }
};

/**
 * Subscribe to job progress updates
 * @param {string} jobId - The job ID to subscribe to
 * @param {object} handlers - Event handlers
 * @param {function} handlers.onProgress - Progress callback (progress: number)
 * @param {function} handlers.onComplete - Completion callback (result: object)
 * @param {function} handlers.onError - Error callback (error: string)
 * @param {function} handlers.onCancelled - Cancellation callback
 * @returns {Promise<boolean>} - Whether subscription was successful
 */
export const subscribeToJob = async (jobId, handlers = {}) => {
    try {
        const conn = await getConnection();

        // Register handlers
        if (handlers.onProgress) eventHandlers.onProgress.set(jobId, handlers.onProgress);
        if (handlers.onComplete) eventHandlers.onComplete.set(jobId, handlers.onComplete);
        if (handlers.onError) eventHandlers.onError.set(jobId, handlers.onError);
        if (handlers.onCancelled) eventHandlers.onCancelled.set(jobId, handlers.onCancelled);

        // Subscribe on server
        await conn.invoke('Subscribe', jobId);
        console.log(`[EA Progress] Subscribed to job: ${jobId}`);
        return true;
    } catch (error) {
        console.error(`[EA Progress] Failed to subscribe to job ${jobId}:`, error);
        return false;
    }
};

/**
 * Unsubscribe from job progress updates
 * @param {string} jobId - The job ID to unsubscribe from
 * @returns {Promise<boolean>} - Whether unsubscription was successful
 */
export const unsubscribeFromJob = async (jobId) => {
    try {
        // Remove handlers
        eventHandlers.onProgress.delete(jobId);
        eventHandlers.onComplete.delete(jobId);
        eventHandlers.onError.delete(jobId);
        eventHandlers.onCancelled.delete(jobId);

        if (connection && connection.state === signalR.HubConnectionState.Connected) {
            await connection.invoke('Unsubscribe', jobId);
            console.log(`[EA Progress] Unsubscribed from job: ${jobId}`);
        }
        return true;
    } catch (error) {
        console.error(`[EA Progress] Failed to unsubscribe from job ${jobId}:`, error);
        return false;
    }
};

/**
 * Check if connected to the hub
 * @returns {boolean}
 */
export const isConnected = () => {
    return connection && connection.state === signalR.HubConnectionState.Connected;
};

/**
 * Get connection state
 * @returns {string}
 */
export const getConnectionState = () => {
    if (!connection) return 'Disconnected';
    switch (connection.state) {
        case signalR.HubConnectionState.Connected:
            return 'Connected';
        case signalR.HubConnectionState.Connecting:
            return 'Connecting';
        case signalR.HubConnectionState.Reconnecting:
            return 'Reconnecting';
        case signalR.HubConnectionState.Disconnected:
            return 'Disconnected';
        default:
            return 'Unknown';
    }
};

/**
 * Disconnect from the hub
 */
export const disconnect = async () => {
    if (connection) {
        try {
            await connection.stop();
            console.log('[EA Progress] Disconnected from hub');
        } catch (error) {
            console.error('[EA Progress] Error disconnecting:', error);
        }
        connection = null;
        connectionPromise = null;

        // Clear all handlers
        eventHandlers.onProgress.clear();
        eventHandlers.onComplete.clear();
        eventHandlers.onError.clear();
        eventHandlers.onCancelled.clear();
    }
};
